import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, OnDestroy, ViewChild, } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.state';
import * as fromStore from '../../../store';
import { distinctUntilChanged, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import * as models from '../../../models';
import { GraphqlService } from '../../../services/graphql.service';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { MapComponent } from '../../../evja/components/map/map.component';
import { DialogService } from 'primeng/dynamicdialog';
import { RenameSensorNodeComponent } from '../rename-sensor-node/rename-sensor-node.component';
import { KeycloakService } from 'keycloak-angular';
import { environment } from 'apps/web-app/src/environments/environment';
import { AnalyticsService } from '../../../@core/utils';


@Component({
  selector: 'pwa-select-sensor-node',
  templateUrl: './select-sensor-node.component.html',
  styleUrls: ['./select-sensor-node.component.scss'],
  providers: [DialogService],
  // EPWA-990: OnPush per evitare NG0100 ExpressionChangedAfterItHasBeenChecked
  // sul host binding 'p-inputwrapper-focus' di p-dropdown durante CD cycles spuri.
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectSensorNodeComponent implements OnInit, OnDestroy {
  mapComponent = MapComponent;
  destroy$: Subject<void> = new Subject<void>();
  destroySubscribeToLastMeasure$: Subject<void> = new Subject<void>();
  sensorNodes$: Observable<models.SensorNode[]> | undefined;
  sensorNodesAdmin: models.SensorNode[] = [];
  sensorNodesUser: models.SensorNode[] = [];
  selectedSensorNodeFormControl = new FormControl();
  editButtonDisabled: boolean = true;

  // EPWA-990: panelStyle forza la larghezza del panel = larghezza trigger (matching demo).
  // Lo style PrimeNG non costringe il panel a matchare il trigger e gli items con max-content
  // lo fanno espandere oltre il trigger.
  @ViewChild('snodeDropdownEl', { read: ElementRef }) snodeDropdownEl?: ElementRef<HTMLElement>;
  panelStyle: { width?: string } = {};


  constructor(
    private store: Store<AppState>,
    private graphqlService: GraphqlService,
    private dialogService: DialogService,
    public translate: TranslateService,
    private keycloakService: KeycloakService,
    private analytics: AnalyticsService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    // this.sensorNodes$ = this.store.select(fromStore.selectAllSensorNodes);

    // EPWA-990: distinctUntilChanged per stabilizzare il riferimento dell'array options
    // del p-dropdown. Senza, ogni re-emissione del selector (anche con stesso contenuto)
    // resettava il virtualScroll a top durante lo scrolling dell'utente.
    // EPWA-1056: il confronto include il nome, altrimenti dopo una rinomina (stesso seriale)
    // l'emissione veniva scartata e il dropdown mostrava il nome vecchio fino al reload.
    const sameNodeList = (prev: models.SensorNode[], curr: models.SensorNode[]) =>
      prev.length === curr.length &&
      prev.every((n, i) => n.serialNumber === curr[i].serialNumber && n.name === curr[i].name);

    if (this.isAdmin()) {
      this.store
        .select(fromStore.selectAllSensorNodes)
        .pipe(
          distinctUntilChanged(sameNodeList),
          map(sensorNode => [...sensorNode].sort((a, b) => a.serialNumber.toLowerCase().localeCompare(b.serialNumber.toLowerCase()))),
          takeUntil(this.destroy$)
        )
        .subscribe(sensorNode => {
          this.sensorNodesAdmin = sensorNode;
          this.cdr.markForCheck();
        });
    } else {
      this.store
        .select(fromStore.selectAllSensorNodes)
        .pipe(
          distinctUntilChanged(sameNodeList),
          map(sensorNode => [...sensorNode].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))),
          takeUntil(this.destroy$)
        )
        .subscribe(sensorNode => {
          this.sensorNodesUser = sensorNode;
          this.cdr.markForCheck();
        });
    }


    this.store
      .select(
        fromStore.selectCurrentSensorNode
      )
      .pipe(
        filter(v => v != undefined),
        distinctUntilChanged())
      .subscribe(selectedSensorNode => {
        this.graphqlService.subscribeToLastMeasure
          (
            {
              sensorNodeSerial: selectedSensorNode!.serialNumber
            }
          )
          .pipe(takeUntil(this.destroySubscribeToLastMeasure$))
          .subscribe(v => this.store.dispatch(fromStore.AddMeasure(
            {
              payload:
              {
                id: v.data.lastMeasure.id,
                type: v.data.lastMeasure.type,
                timestamp: v.data.lastMeasure.timestamp,
                value: +v.data.lastMeasure.value
              }
            }
          )));
        // EPWA-990: setValue posticipato fuori dal CD cycle corrente per evitare
        // NG0100 (ExpressionChangedAfterItHasBeenChecked) su 'p-inputwrapper-focus'
        // di PrimeNG p-dropdown, che a sua volta lasciava il dropdown in stato
        // inconsistente e bloccava il click successivo in dev build.
        // emitEvent:false evita anche il loop spurio store → setValue → valueChanges → dispatch.
        setTimeout(() => {
          this.selectedSensorNodeFormControl.setValue(selectedSensorNode, { emitEvent: false });
          this.editButtonDisabled = false;
          this.cdr.markForCheck();
        });
      });
    this.selectedSensorNodeFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((node: models.SensorNode) => {
        this.store.dispatch(fromStore.ChangeSelectedSensorNodeSerialNumber({ sensorNode: node.serialNumber }));
        this.store.dispatch(fromStore.LoadNotifications({ payload: { username: this.keycloakService.getUsername(), language: this.translate.currentLang } }))
        this.destroySubscribeToLastMeasure$.next();
        this.graphqlService.subscribeToLastMeasure
          (
            {
              sensorNodeSerial: node.serialNumber
            }
          )
          .pipe(takeUntil(this.destroySubscribeToLastMeasure$))
          .subscribe(v => this.store.dispatch(fromStore.AddMeasure(
            {
              payload: {
                id: v.data.lastMeasure.id,
                type: v.data.lastMeasure.type,
                timestamp: v.data.lastMeasure.timestamp,
                value: +v.data.lastMeasure.value
              }
            }

          )));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroySubscribeToLastMeasure$.next();
    this.destroySubscribeToLastMeasure$.complete();
  }

  open() {
    this.dialogService.open(RenameSensorNodeComponent,
      {
        header: `${this.translate.instant('COMMON.CHANGESNODENAME')}`,
        width: '100%',
        baseZIndex: 10000
      });
  }

  isAdmin(): boolean {
    return this.keycloakService.isUserInRole(environment.keycloak.config.adminRole);
  }

  getEventHeader(eventName: string, eventParams?: {
    [key: string]: any;
  }) {
    this.analytics.trackEvent(eventName, eventParams);
  }

  /** Computa la width del trigger del dropdown e la propaga al panelStyle. */
  syncPanelWidth(): void {
    const triggerEl = this.snodeDropdownEl?.nativeElement?.querySelector('.p-dropdown') as HTMLElement | null;
    if (triggerEl) {
      this.panelStyle = { width: triggerEl.offsetWidth + 'px' };
      this.cdr.markForCheck();
    }
    // EPWA-990: reset scrollLeft del wrapper a 0 — PrimeNG dopo onShow scrolla
    // all'item selezionato anche orizzontalmente quando items.width > wrapper.width,
    // mostrando il finale del nome ("VITO ..." invece di "NICOLA VITO ..."). Forziamo
    // a 0 dopo il render del panel.
    setTimeout(() => {
      const wrapper = document.querySelector('.p-dropdown-panel.snode-dropdown-panel .p-dropdown-items-wrapper') as HTMLElement | null;
      if (wrapper) wrapper.scrollLeft = 0;
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncPanelWidth();
  }

}
